/**
 * Frontend mood conversion utility for converting numeric mood values to human-readable text.
 * Simplified to only handle integers 1-5 as requested.
 */

/**
 * Convert numeric mood value (1-5) to human-readable mood description
 * Simplified to only handle integers 1-5 as requested
 *
 * @param {number|string} moodValue - Numeric mood value (1-5) or existing text
 * @returns {string} Human-readable mood description or original value if not convertible
 *
 * @example
 * convertMoodValue(1) // "Sad"
 * convertMoodValue(5) // "Excited"
 * convertMoodValue("Happy") // "Happy"
 * convertMoodValue(3) // "Calm"
 */
export const convertMoodValue = (moodValue) => {
  // Handle null, undefined, or empty values
  if (moodValue == null || moodValue === '') {
    return moodValue;
  }

  // Handle string numbers
  if (typeof moodValue === 'string') {
    // If it's already text (not a number), return as-is
    if (isNaN(Number(moodValue))) {
      return moodValue;
    }

    // Try to convert string number to integer
    const numValue = parseInt(moodValue, 10);
    if (numValue >= 1 && numValue <= 5) {
      const moodMap = {
        1: 'Sad',
        2: 'Angry',
        3: 'Calm',
        4: 'Happy',
        5: 'Excited'
      };
      return moodMap[numValue];
    }

    // Return original value if not in 1-5 range
    return moodValue;
  }

  // Handle numeric values (only integers 1-5)
  if (typeof moodValue === 'number' && Number.isInteger(moodValue)) {
    if (moodValue >= 1 && moodValue <= 5) {
      const moodMap = {
        1: 'Sad',
        2: 'Angry',
        3: 'Calm',
        4: 'Happy',
        5: 'Excited'
      };
      return moodMap[moodValue];
    }

    // Return original value if outside 1-5 range
    return String(moodValue);
  }

  // For any other type, return as string
  return String(moodValue);
};

/**
 * Check if a value is a mood field that should be converted
 *
 * @param {string} fieldName - The field name to check
 * @returns {boolean} True if the field should be converted
 */
export const isMoodField = (fieldName) => {
  if (!fieldName || typeof fieldName !== 'string') {
    return false;
  }

  const moodFields = ['mood', 'moodScore', 'patientMood', 'current_mood'];
  return moodFields.some(moodField =>
    fieldName.toLowerCase().includes(moodField.toLowerCase())
  );
};